import React, { useState, useEffect } from 'react';
import { CompletionCard, NextOfKinCard, AdminApplyCard } from '../components/SettingsExtras';
import { LANGUAGES, getLang, setLang } from '../i18n';
import MapPickerModal from '../components/MapPickerModal';

const INTERESTS_LIST = [
  '✍️ Writing', '📖 Reading', '💰 Crypto', '🥧 Pi Network', '🎮 Gaming',
  '🎵 Music', '🎨 Art', '📸 Photography', '🏃 Fitness', '🍕 Food',
  '✈️ Travel', '💻 Technology', '📈 Investing', '🎬 Movies', '⚽ Sports',
  '🌿 Nature', '🔬 Science', '📚 Education', '🎯 Entrepreneurship', '🤖 AI'
];

export default function Settings({ user, onAvatarUpdate }) {
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState('');
  const [dob, setDob] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [location, setLocation] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [discordCode, setDiscordCode] = useState('');
  const [telegramCode, setTelegramCode] = useState('');
  const [generatingCode, setGeneratingCode] = useState(false);
  const [discordStatus, setDiscordStatus] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState(null);

  const fetchDiscordStatus = React.useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/discord/status/', {
        headers: { Authorization: `Token ${user.token}` },
      });
      setDiscordStatus(await res.json());
    } catch (e) {}
  }, [user]);

  const fetchTelegramStatus = React.useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/telegram/status/', {
        headers: { Authorization: `Token ${user.token}` },
      });
      setTelegramStatus(await res.json());
    } catch (e) {}
  }, [user]);

  useEffect(() => { fetchDiscordStatus(); }, [fetchDiscordStatus]);
  useEffect(() => { fetchTelegramStatus(); }, [fetchTelegramStatus]);

  const generateDiscordCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await fetch('/api/discord/generate-code/', {
        method: 'POST', headers: { Authorization: `Token ${user.token}` },
      });
      const data = await res.json();
      setDiscordCode(data.code || '');
    } catch (e) {}
    setGeneratingCode(false);
  };

  const generateTelegramCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await fetch('/api/telegram/generate-code/', {
        method: 'POST', headers: { Authorization: `Token ${user.token}` },
      });
      const data = await res.json();
      setTelegramCode(data.code || '');
    } catch (e) {}
    setGeneratingCode(false);
  };

  const disconnectDiscord = async () => {
    if (!window.confirm('Disconnect your Discord account?')) return;
    try {
      await fetch('/api/discord/disconnect/', {
        method: 'POST', headers: { Authorization: `Token ${user.token}` },
      });
      setDiscordStatus({ linked: false });
      setDiscordCode('');
    } catch (e) {}
  };

  const disconnectTelegram = async () => {
    if (!window.confirm('Disconnect your Telegram account?')) return;
    try {
      await fetch('/api/telegram/disconnect/', {
        method: 'POST', headers: { Authorization: `Token ${user.token}` },
      });
      setTelegramStatus({ linked: false });
      setTelegramCode('');
    } catch (e) {}
  };
  const [isPrivate, setIsPrivate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');
  const [lang, setLangState] = useState(getLang());
  const [appTheme, setAppThemeState] = useState(localStorage.getItem('xpert_theme') || 'midnight');
  const setAppTheme = (theme) => {
    localStorage.setItem('xpert_theme', theme);
    setAppThemeState(theme);
  };

  useEffect(() => { if (user) fetchProfile(); }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`/api/profile/${user.username}/`);
      const data = await res.json();
      setProfile(data);
      setBio(data.bio || '');
      setDob(data.date_of_birth || '');
      setSelectedInterests(data.interests ? data.interests.split(',').map(i => i.trim()).filter(Boolean) : []);
      setLocation(data.location || '');
      setWebsite(data.website || '');
      setPhone(data.phone || '');
      setAddress(data.address || '');
      setCountry(data.country || '');
      setLatitude(data.latitude || null);
      setLongitude(data.longitude || null);
      setIsPrivate(data.is_private || false);
    } catch (err) { console.error(err); }
  };

  const toggleInterest = (interest) => {
    setSelectedInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  const formatDOB = (val) => {
    if (!val) return '';
    const d = new Date(val + 'T00:00:00');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/profile/update/', {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bio,
          date_of_birth: dob,
          interests: selectedInterests.join(', '),
          location,
          website,
          phone,
          address,
          country,
          latitude,
          longitude,
          is_private: isPrivate
        })
      });
      const data = await res.json();
      if (data.message) {
        setMsg('✅ Profile saved successfully!');
        setMsgType('success');
        // Update profile state without refetch
        setProfile(prev => ({ ...prev, bio, date_of_birth: dob, interests: selectedInterests.join(', '), location, website, phone, address, country, latitude, longitude, is_private: isPrivate }));
      } else {
        setMsg('❌ ' + (data.error || 'Error saving'));
        setMsgType('error');
      }
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('❌ Network error');
      setMsgType('error');
      setTimeout(() => setMsg(''), 3000);
    }
    setSaving(false);
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const res = await fetch('/api/profile/avatar/', {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}` },
        body: formData
      });
      const data = await res.json();
      if (data.avatar) {
        onAvatarUpdate && onAvatarUpdate(data.avatar);
        setMsg('✅ Avatar updated!');
        setMsgType('success');
        setTimeout(() => setMsg(''), 3000);
        fetchProfile();
      }
    } catch (err) { console.error(err); }
  };

  if (!user) return <div style={S.container}><h2 style={{ textAlign: 'center', color: '#6200ea' }}>🔑 Login Required</h2></div>;

  return (
    <div style={S.container}>
      <h1 style={S.title}>⚙️ Settings</h1>

      {msg && (
        <div style={{ ...S.msg, backgroundColor: msgType === 'success' ? '#d1fae5' : '#fee2e2', color: msgType === 'success' ? '#065f46' : '#991b1b' }}>
          {msg}
        </div>
      )}


      {/* Theme */}
      <CompletionCard />
      <NextOfKinCard />
      <AdminApplyCard />
      <div style={S.card}>
        <h3 style={S.cardTitle}>🎨 Theme</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <button onClick={() => setAppTheme('midnight')} style={{ background: 'linear-gradient(135deg,#1a3a6e,#070b14)', border: appTheme === 'midnight' ? '2px solid #1d4ed8' : '2px solid transparent', borderRadius: 12, padding: '14px 8px', cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: 11 }}>🌙 Midnight Blue</button>
          <button onClick={() => setAppTheme('forest')} style={{ background: 'linear-gradient(135deg,#16a34a,#071410)', border: appTheme === 'forest' ? '2px solid #16a34a' : '2px solid transparent', borderRadius: 12, padding: '14px 8px', cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: 11 }}>🌿 Dark Forest</button>
          <button onClick={() => setAppTheme('rose')} style={{ background: 'linear-gradient(135deg,#be123c,#140b0f)', border: appTheme === 'rose' ? '2px solid #be123c' : '2px solid transparent', borderRadius: 12, padding: '14px 8px', cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: 11 }}>🌹 Dark Rose</button>
        </div>
        <p style={{ fontSize: 11, color: '#999', marginTop: 8, textAlign: 'center' }}>Changes the Home page background.</p>
      </div>

      {/* Discord Linking */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>💬 Link Discord</h3>
        {discordStatus === null ? (
          <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>
        ) : discordStatus.linked ? (
          <div>
            <p style={{ fontSize: 13, margin: '0 0 10px' }}>
              ✅ Connected as <strong>@{discordStatus.discord_username}</strong> (Pi user: <strong>@{user.username}</strong>)
            </p>
            <button onClick={disconnectDiscord} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: '1px solid #A32D2D', borderRadius: 8, background: '#fff', color: '#A32D2D', cursor: 'pointer' }}>
              Disconnect
            </button>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>
              Generate a code, then type <code>/verify CODE</code> in the PenFlow Discord server to link your account and get your role (Merchant, Freelancer, Pet Holder, etc.) automatically.
            </p>
            <button onClick={generateDiscordCode} disabled={generatingCode} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>
              {generatingCode ? 'Generating...' : 'Generate Code'}
            </button>
            {discordCode && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: '#E1F5EE', borderRadius: 8, textAlign: 'center' }}>
                <p style={{ fontSize: 20, fontWeight: 700, letterSpacing: 2, margin: '0 0 4px', fontFamily: 'monospace' }}>{discordCode}</p>
                <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Type <code>/verify {discordCode}</code> in Discord within 10 minutes.</p>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Telegram Linking */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>📱 Link Telegram</h3>
        {telegramStatus === null ? (
          <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>
        ) : telegramStatus.linked ? (
          <div>
            <p style={{ fontSize: 13, margin: '0 0 10px' }}>
              ✅ Connected as <strong>@{telegramStatus.telegram_username}</strong> (Pi user: <strong>@{user.username}</strong>)
            </p>
            <button onClick={disconnectTelegram} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: '1px solid #A32D2D', borderRadius: 8, background: '#fff', color: '#A32D2D', cursor: 'pointer' }}>
              Disconnect
            </button>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>
              Generate a code, then type <code>/verify CODE</code> in the PenFlow Telegram bot @XpertCreation_Bot to link your account and get your role (Merchant, Freelancer, Pet Holder, etc.) automatically.
            </p>
            <button onClick={generateTelegramCode} disabled={generatingCode} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>
              {generatingCode ? 'Generating...' : 'Generate Code'}
            </button>
            {telegramCode && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: '#E1F5EE', borderRadius: 8, textAlign: 'center' }}>
                <p style={{ fontSize: 20, fontWeight: 700, letterSpacing: 2, margin: '0 0 4px', fontFamily: 'monospace' }}>{telegramCode}</p>
                <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Type <code>/verify {telegramCode}</code> to @XpertCreation_Bot within 10 minutes.</p>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Avatar */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>📸 Profile Picture</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {profile?.avatar
            ? <img src={profile.avatar} alt="avatar" style={S.avatarBig} />
            : <div style={S.avatarPlaceholder}>{user.username[0].toUpperCase()}</div>
          }
          <div>
            <label style={S.uploadBtn}>
              📁 Choose Photo
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadAvatar} />
            </label>
            <p style={{ color: '#999', fontSize: '12px', margin: '6px 0 0' }}>JPG, PNG — Max 2MB</p>
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>👤 Profile Info</h3>

        <label style={S.label}>Bio</label>
        <textarea style={S.input} rows={3} placeholder="Tell others about yourself..." value={bio} onChange={e => setBio(e.target.value)} />

        <label style={S.label}>📅 Date of Birth {dob && <span style={{ color: '#6200ea', fontWeight: 'bold' }}>({formatDOB(dob)})</span>}</label>
        <input style={{ ...S.input, colorScheme: 'light' }} type="date" value={dob} onChange={e => setDob(e.target.value)} />
        {dob && <div style={{ color: '#6200ea', fontSize: '13px', marginTop: '-10px', marginBottom: '14px' }}>📅 {formatDOB(dob)}</div>}

        <label style={S.label}>📍 Location</label>
        <input style={S.input} placeholder="e.g. Lahore, Pakistan" value={location} onChange={e => setLocation(e.target.value)} />

        <label style={S.label}>🌐 Website</label>
        <input style={S.input} placeholder="https://yourwebsite.com" value={website} onChange={e => setWebsite(e.target.value)} />
        <label style={S.label}>🌍 Country <span style={{ color: '#999', fontWeight: 'normal' }}>(used to show local currency prices next to Pi amounts)</span></label>
        <select style={S.input} value={country} onChange={e => setCountry(e.target.value)}>
          <option value="">— Select your country —</option>
          {['Pakistan', 'India', 'United States', 'United Kingdom', 'United Arab Emirates', 'Saudi Arabia', 'Bangladesh', 'Canada', 'Australia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Malaysia', 'Turkey', 'Germany', 'France', 'Nigeria', 'Egypt', 'Indonesia', 'Philippines', 'China', 'Japan'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <label style={{display:"block",margin:"12px 0 4px",fontWeight:"bold"}}>📞 Phone</label>
        <input style={S.input} placeholder="+92 3xx xxxxxxx" value={phone} onChange={e => setPhone(e.target.value)} />
        <label style={{display:"block",margin:"12px 0 4px",fontWeight:"bold"}}>🏠 Address</label>
        <input style={S.input} placeholder="Street, City, Country" value={address} onChange={e => setAddress(e.target.value)} />
        <button
          type="button"
          onClick={() => setShowMapPicker(true)}
          style={{ marginTop: '6px', marginBottom: '14px', padding: '8px 14px', fontSize: '13px', fontWeight: 500, border: 'none', borderRadius: '8px', background: '#E1F5EE', color: '#0F6E56', cursor: 'pointer' }}
        >
          🗺️ Pick on map / use current location
        </button>
        {showMapPicker && (
          <MapPickerModal
            initialLat={latitude}
            initialLng={longitude}
            onClose={() => setShowMapPicker(false)}
            onConfirm={({ address: pickedAddress, latitude: lat, longitude: lng }) => {
              setAddress(pickedAddress);
              setLatitude(lat);
              setLongitude(lng);
              setShowMapPicker(false);
            }}
          />
        )}
      </div>

      {/* Interests */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>🎯 Interests ({selectedInterests.length} selected)</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {INTERESTS_LIST.map(interest => (
            <div key={interest} onClick={() => toggleInterest(interest)} style={{
              padding: '7px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
              backgroundColor: selectedInterests.includes(interest) ? '#6200ea' : '#f3e5f5',
              color: selectedInterests.includes(interest) ? 'white' : '#6200ea',
              border: `2px solid ${selectedInterests.includes(interest) ? '#6200ea' : '#e0e0e0'}`,
              transition: 'all 0.2s'
            }}>
              {selectedInterests.includes(interest) ? '✓ ' : ''}{interest}
            </div>
          ))}
        </div>
      </div>

      {/* Privacy */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>🔒 Privacy</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 'bold', color: '#333' }}>Private Profile</div>
            <div style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>Only connected users can see your profile</div>
          </div>
          <div onClick={() => setIsPrivate(!isPrivate)} style={{
            width: '48px', height: '26px', borderRadius: '13px',
            backgroundColor: isPrivate ? '#6200ea' : '#ccc',
            cursor: 'pointer', position: 'relative', transition: 'all 0.3s'
          }}>
            <div style={{
              width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'white',
              position: 'absolute', top: '2px', transition: 'all 0.3s',
              left: isPrivate ? '24px' : '2px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />
          </div>
        </div>
      </div>

      {/* Language */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>🌐 Language / زبان</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {Object.entries(LANGUAGES).map(([code, info]) => (
            <div key={code} onClick={() => { setLang(code); setLangState(code); document.documentElement.dir = info.dir; }} style={{
              padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px',
              backgroundColor: lang === code ? '#6200ea' : '#f3e5f5',
              color: lang === code ? 'white' : '#6200ea',
              border: `2px solid ${lang === code ? '#6200ea' : '#e0e0e0'}`,
              fontWeight: lang === code ? 'bold' : 'normal',
            }}>
              {info.flag} {info.name}
            </div>
          ))}
        </div>
        <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>Restart app after changing language</p>
      </div>

      <button style={S.saveBtn} onClick={saveProfile} disabled={saving}>
        {saving ? '⏳ Saving...' : '✅ Save Changes'}
      </button>
    </div>
  );
}

const S = {
  container: { maxWidth: '700px', margin: '20px auto', padding: '20px' },
  title: { color: '#6200ea', marginBottom: '20px' },
  card: { backgroundColor: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  cardTitle: { color: '#6200ea', margin: '0 0 16px', fontSize: '16px' },
  label: { display: 'block', color: '#666', fontSize: '13px', marginBottom: '6px', fontWeight: '500' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '10px', border: '2px solid #e0e0e0', fontSize: '14px', marginBottom: '14px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },
  avatarBig: { width: '70px', height: '70px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #6200ea' },
  avatarPlaceholder: { width: '70px', height: '70px', borderRadius: '50%', backgroundColor: '#6200ea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 'bold' },
  uploadBtn: { backgroundColor: '#6200ea', color: 'white', padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', display: 'inline-block' },
  saveBtn: { width: '100%', backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '16px', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '30px' },
  msg: { padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', textAlign: 'center', fontWeight: 'bold' },
};
